import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import {
  FileCheck2,
  Download,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  FileSpreadsheet,
  FileCode,
  Send,
  Lock,
  Sparkles,
  Calendar,
  Layers,
  ChevronRight,
  Key,
  ShieldAlert,
  HelpCircle,
  RefreshCw
} from 'lucide-react';
import { useGst } from '../context/GstContext';
import {
  generateGstr1,
  generateGstr3b,
  generateGstr9AnnualSummary,
  formatGstnReturnPeriod
} from '../services/returnGeneratorService';
import {
  requestGspOtp,
  authenticateGspSession,
  saveGstr1ToGsp,
  saveGstr3bToGsp,
  proceedToFileGsp,
  fileReturnWithEvc
} from '../services/gspConnectorService';
import { GspAuthSession } from '../types/gst';
import { toast } from '../components/ui';

export const ReturnsPage: React.FC = () => {
  const { invoices, gstr2bRecords, activeGstin, financialYear, taxPeriod, clientLegalName } = useGst();

  const [activeTab, setActiveTab] = useState<'GSTR-1' | 'GSTR-2B' | 'GSTR-3B' | 'GSTR-9'>('GSTR-3B');
  const [showGspModal, setShowGspModal] = useState<boolean>(false);

  // GSP Filing Lifecycle States (Adapted from India Compliance ReturnsAPI)
  const [gspUsername, setGspUsername] = useState<string>('verve_taxpayer');
  const [gspSession, setGspSession] = useState<GspAuthSession | null>(null);
  const [portalOtp, setPortalOtp] = useState<string>('');
  const [evcOtp, setEvcOtp] = useState<string>('');
  const [filingStep, setFilingStep] = useState<'AUTH' | 'VALIDATE' | 'EVC' | 'SUCCESS'>('AUTH');
  const [filingLoading, setFilingLoading] = useState<boolean>(false);
  const [filingStatusMsg, setFilingStatusMsg] = useState<string>('');
  const [generatedArn, setGeneratedArn] = useState<string>('');
  const [filingDate, setFilingDate] = useState<string>('');

  const gstr1 = generateGstr1(activeGstin || "27AABCU9603R1ZM", financialYear, taxPeriod, invoices);
  const gstr3b = generateGstr3b(activeGstin || "27AABCU9603R1ZM", financialYear, taxPeriod, invoices, invoices, gstr2bRecords);
  const gstr9 = generateGstr9AnnualSummary(activeGstin || "27AABCU9603R1ZM", financialYear, invoices, gstr2bRecords);

  const fp = formatGstnReturnPeriod(taxPeriod, financialYear);

  // 1. Export Official GSTN JSON
  const handleExportJson = () => {
    let payload: any;
    let filename = "";

    if (activeTab === 'GSTR-1') {
      payload = gstr1.gstnJsonPayload;
      filename = `GSTR1_${activeGstin}_${fp}.json`;
    } else if (activeTab === 'GSTR-3B') {
      payload = gstr3b.gstnJsonPayload;
      filename = `GSTR3B_${activeGstin}_${fp}.json`;
    } else {
      payload = gstr9;
      filename = `GSTR9_ANNUAL_${activeGstin}_${financialYear}.json`;
    }

    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(payload, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", filename);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
    toast.success(`Exported official GSTN ${activeTab} JSON payload`);
  };

  // 2. Export CA Audit Excel
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: GSTR-3B Tax Summary
    const gstr3bRows = [
      ["GSTR-3B Tax Summary (India Compliance Engine)", `${taxPeriod} ${financialYear}`],
      ["GSTIN", activeGstin],
      ["Legal Name", clientLegalName],
      [],
      ["Table 3.1: Outward Supplies & Reverse Charge", "Taxable Value (₹)", "IGST (₹)", "CGST (₹)", "SGST (₹)", "Cess (₹)"],
      [
        "3.1(a) Outward Taxable Supplies",
        gstr3b.table3_1_outward_taxable.taxable_value,
        gstr3b.table3_1_outward_taxable.igst,
        gstr3b.table3_1_outward_taxable.cgst,
        gstr3b.table3_1_outward_taxable.sgst,
        gstr3b.table3_1_outward_taxable.cess
      ],
      [
        "3.1(d) Inward Supplies Liable to RCM",
        gstr3b.table3_1_inward_rcm.taxable_value,
        gstr3b.table3_1_inward_rcm.igst,
        gstr3b.table3_1_inward_rcm.cgst,
        gstr3b.table3_1_inward_rcm.sgst,
        gstr3b.table3_1_inward_rcm.cess
      ],
      [],
      ["Table 4: Eligible ITC (Reconciled from GSTR-2B)", "Amount (₹)"],
      ["All Other ITC (Matched in GSTR-2B)", gstr3b.table4_eligible_itc.all_other_itc_2b],
      ["Inward RCM Tax Credit", gstr3b.table4_eligible_itc.inward_rcm],
      ["Total Eligible ITC Available (Table 4.C)", gstr3b.table4_eligible_itc.total_eligible_itc],
      ["Ineligible / Blocked under Sec 17(5) (Table 4.D)", gstr3b.table4_ineligible_itc_17_5],
      [],
      ["Table 6.1: Net Tax Payable in Cash", "Amount (₹)"],
      ["Net IGST Payable", gstr3b.net_tax_payable.igst],
      ["Net CGST Payable", gstr3b.net_tax_payable.cgst],
      ["Net SGST Payable", gstr3b.net_tax_payable.sgst],
      ["Net Cess Payable", gstr3b.net_tax_payable.cess]
    ];
    const ws3b = XLSX.utils.aoa_to_sheet(gstr3bRows);
    XLSX.utils.book_append_sheet(wb, ws3b, "GSTR-3B_Summary");

    // Sheet 2: GSTR-1 Sales
    const gstr1Rows = [
      ["Invoice Number", "Date", "Customer GSTIN", "Taxable Value (₹)", "Total Tax (₹)", "Invoice Total (₹)"]
    ];
    for (const inv of invoices.filter(i => i.category === 'B2B_SALES' || i.category === 'B2C_RETAIL')) {
      gstr1Rows.push([
        inv.invoice_number,
        inv.invoice_date,
        inv.buyer_gstin || 'URP',
        (inv.total_taxable_value || 0).toString(),
        (inv.total_tax || 0).toString(),
        (inv.invoice_total || 0).toString()
      ]);
    }
    const ws1 = XLSX.utils.aoa_to_sheet(gstr1Rows);
    XLSX.utils.book_append_sheet(wb, ws1, "GSTR-1_Sales");

    // Sheet 3: GSTR-9 Annual Consolidation
    const gstr9Rows = [
      ["Month", "Sales Taxable (₹)", "Output Tax (₹)", "ITC Claimed 3B (₹)", "ITC in 2B (₹)", "Variance (₹)"],
      ...gstr9.monthlyBreakdown.map(m => [
        m.month,
        m.salesTaxable.toString(),
        m.outputTax.toString(),
        m.itcClaimed.toString(),
        m.itcIn2B.toString(),
        m.variance.toString()
      ])
    ];
    const ws9 = XLSX.utils.aoa_to_sheet(gstr9Rows);
    XLSX.utils.book_append_sheet(wb, ws9, "GSTR-9_Annual");

    XLSX.writeFile(wb, `CA_AUDIT_REPORT_${activeGstin}_${taxPeriod}_${financialYear}.xlsx`);
    toast.success("CA Audit Excel report generated successfully");
  };

  // 3. GSP Live Workflow Handlers
  const handleRequestOtp = async () => {
    try {
      setFilingLoading(true);
      setFilingStatusMsg('Requesting GSTN Portal OTP via GSP Gateway...');
      const res = await requestGspOtp(activeGstin || "27AABCU9603R1ZM", gspUsername);
      toast.success(res.message);
      setFilingStatusMsg('OTP sent to registered mobile/email.');
    } catch (err: any) {
      toast.error(err.message);
      setFilingStatusMsg(err.message);
    } finally {
      setFilingLoading(false);
    }
  };

  const handleAuthenticateSession = async () => {
    if (!portalOtp || portalOtp.length < 4) {
      toast.error('Please enter the OTP sent by GSTN.');
      return;
    }
    try {
      setFilingLoading(true);
      setFilingStatusMsg('Validating OTP and obtaining GSP session token...');
      const session = await authenticateGspSession(activeGstin || "27AABCU9603R1ZM", gspUsername, portalOtp);
      setGspSession(session);
      setFilingStep('VALIDATE');
      toast.success('GSP session authenticated successfully!');
      setFilingStatusMsg('Session established. Ready to validate & lock return.');
    } catch (err: any) {
      toast.error(err.message);
      setFilingStatusMsg(err.message);
    } finally {
      setFilingLoading(false);
    }
  };

  const handleUploadAndProceedToFile = async () => {
    if (!gspSession) return;
    try {
      setFilingLoading(true);
      setFilingStatusMsg(`Uploading official ${activeTab} JSON payload to GSTN...`);

      if (activeTab === 'GSTR-1') {
        await saveGstr1ToGsp(gspSession, gstr1.gstnJsonPayload);
        await proceedToFileGsp(gspSession, 'GSTR1', fp);
      } else {
        await saveGstr3bToGsp(gspSession, gstr3b.gstnJsonPayload);
        await proceedToFileGsp(gspSession, 'GSTR3B', fp);
      }

      setFilingStep('EVC');
      toast.success(`${activeTab} verified and liability locked. Enter Signatory EVC.`);
      setFilingStatusMsg('Liabilities locked on GST portal. Enter Aadhaar EVC to sign.');
    } catch (err: any) {
      toast.error(err.message);
      setFilingStatusMsg(err.message);
    } finally {
      setFilingLoading(false);
    }
  };

  const handleFinalSignWithEvc = async () => {
    if (!gspSession) return;
    if (!evcOtp || evcOtp.length < 4) {
      toast.error('Please enter the 6-digit Signatory EVC OTP.');
      return;
    }
    try {
      setFilingLoading(true);
      setFilingStatusMsg('Signing return and generating Application Reference Number (ARN)...');
      const retType = activeTab === 'GSTR-1' ? 'GSTR1' : 'GSTR3B';
      const pan = (activeGstin || "27AABCU9603R1ZM").substring(2, 12);

      const res = await fileReturnWithEvc(gspSession, retType, fp, evcOtp, pan);
      if (res.success && res.arn) {
        setGeneratedArn(res.arn);
        setFilingDate(res.filingDate || new Date().toISOString());
        setFilingStep('SUCCESS');
        toast.success(`Success! ${activeTab} filed. ARN: ${res.arn}`);
      } else {
        throw new Error(res.message || 'Filing was rejected by GSTN.');
      }
    } catch (err: any) {
      toast.error(err.message);
      setFilingStatusMsg(err.message);
    } finally {
      setFilingLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Title & Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <FileCheck2 className="h-6 w-6 text-cyan-400" />
            <span>Auto-Populated Returns & 1-Click Annual Dashboard</span>
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Powered by resilient-tech/india-compliance schemas with direct GSP filing & official GSTN exports
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleExportJson}
            className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-[#131316] hover:bg-white/5 px-3 py-2 text-xs font-semibold text-white transition"
          >
            <FileCode className="h-4 w-4 text-cyan-400" />
            <span>Export Official GSTN JSON</span>
          </button>

          <button
            onClick={handleExportExcel}
            className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-[#131316] hover:bg-white/5 px-3 py-2 text-xs font-semibold text-white transition"
          >
            <FileSpreadsheet className="h-4 w-4 text-emerald-400" />
            <span>Download CA Audit Excel</span>
          </button>

          <button
            onClick={() => {
              setShowGspModal(true);
              setFilingStep('AUTH');
              setGeneratedArn('');
              setFilingStatusMsg('');
            }}
            className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-3.5 py-2 text-xs font-bold text-black transition hover:opacity-90"
          >
            <Send className="h-4 w-4" />
            <span>1-Click Direct GSP Filing (OTP)</span>
          </button>
        </div>
      </div>

      {/* Visual Auto-Population Pipeline */}
      <div className="rounded-2xl border border-white/10 bg-[#131316] p-4 text-xs">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center justify-between">
          <span>End-to-End India Compliance Pipeline</span>
          <span className="text-cyan-400 font-mono">GSP Protocol: Active</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-center">
          {/* Step 1 */}
          <div className="rounded-xl border border-white/5 bg-[#0e1015] p-3 text-center space-y-1">
            <div className="font-bold text-white">1. Verified Ledger</div>
            <div className="text-[11px] text-slate-400">
              {invoices.length} Verified Invoices & Bills
            </div>
            <div className="text-[10px] text-emerald-400 font-bold">● Extracted & Checked</div>
          </div>

          <div className="hidden sm:flex justify-center text-slate-500">
            <ArrowRight className="h-5 w-5" />
          </div>

          {/* Step 2 */}
          <div className="rounded-xl border border-white/5 bg-[#0e1015] p-3 text-center space-y-1">
            <div className="font-bold text-white">2. GSTR-1 & 2B Recon</div>
            <div className="text-[11px] text-slate-400">
              ₹{gstr3b.table3_1_outward_taxable.taxable_value.toLocaleString('en-IN')} Sales &bull; ₹{gstr3b.table4_eligible_itc.total_eligible_itc.toLocaleString('en-IN')} Matched ITC
            </div>
            <div className="text-[10px] text-cyan-400 font-bold">● Auto-Populated</div>
          </div>

          <div className="hidden sm:flex justify-center text-slate-500">
            <ArrowRight className="h-5 w-5" />
          </div>

          {/* Step 3 */}
          <div className="rounded-xl border border-white/5 bg-[#0e1015] p-3 text-center space-y-1">
            <div className="font-bold text-white">3. GSTR-3B & 9 Matrix</div>
            <div className="text-[11px] text-slate-400">
              Net Tax: ₹{(gstr3b.net_tax_payable.igst + gstr3b.net_tax_payable.cgst + gstr3b.net_tax_payable.sgst).toLocaleString('en-IN')}
            </div>
            <div className="text-[10px] text-amber-400 font-bold">● Ready for GSP / Portal</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-white/10 pb-3 text-xs">
        {(['GSTR-3B', 'GSTR-1', 'GSTR-2B', 'GSTR-9'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`rounded-xl px-4 py-2 font-bold transition ${
              activeTab === tab
                ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* TAB 1: GSTR-3B Details */}
      {activeTab === 'GSTR-3B' && (
        <div className="space-y-6">
          {/* Table 3.1 Outward Supplies */}
          <div className="rounded-2xl border border-white/10 bg-[#131316] p-5 space-y-3">
            <div className="flex items-center justify-between border-b border-white/10 pb-2">
              <h2 className="font-bold text-white text-sm">Table 3.1: Details of Outward Supplies and Inward Supplies Liable to Reverse Charge</h2>
              <span className="text-[11px] font-mono text-cyan-400">Official GSTN Section 3.1</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs font-mono">
                <thead>
                  <tr className="border-b border-white/5 text-slate-400 font-sans">
                    <th className="py-2 px-3">Nature of Supplies</th>
                    <th className="py-2 px-3 text-right">Total Taxable Value (₹)</th>
                    <th className="py-2 px-3 text-right">IGST (₹)</th>
                    <th className="py-2 px-3 text-right">CGST (₹)</th>
                    <th className="py-2 px-3 text-right">SGST (₹)</th>
                    <th className="py-2 px-3 text-right">Cess (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-slate-200">
                  <tr>
                    <td className="py-2.5 px-3 font-sans">
                      <div className="font-semibold text-white">(a) Outward taxable supplies (other than zero-rated, nil and exempted)</div>
                    </td>
                    <td className="py-2.5 px-3 text-right">{gstr3b.table3_1_outward_taxable.taxable_value.toFixed(2)}</td>
                    <td className="py-2.5 px-3 text-right">{gstr3b.table3_1_outward_taxable.igst.toFixed(2)}</td>
                    <td className="py-2.5 px-3 text-right">{gstr3b.table3_1_outward_taxable.cgst.toFixed(2)}</td>
                    <td className="py-2.5 px-3 text-right">{gstr3b.table3_1_outward_taxable.sgst.toFixed(2)}</td>
                    <td className="py-2.5 px-3 text-right">{gstr3b.table3_1_outward_taxable.cess.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 px-3 font-sans">
                      <div className="font-semibold text-white">(d) Inward supplies liable to reverse charge (RCM)</div>
                    </td>
                    <td className="py-2.5 px-3 text-right">{gstr3b.table3_1_inward_rcm.taxable_value.toFixed(2)}</td>
                    <td className="py-2.5 px-3 text-right">{gstr3b.table3_1_inward_rcm.igst.toFixed(2)}</td>
                    <td className="py-2.5 px-3 text-right">{gstr3b.table3_1_inward_rcm.cgst.toFixed(2)}</td>
                    <td className="py-2.5 px-3 text-right">{gstr3b.table3_1_inward_rcm.sgst.toFixed(2)}</td>
                    <td className="py-2.5 px-3 text-right">{gstr3b.table3_1_inward_rcm.cess.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Table 4 Eligible ITC */}
          <div className="rounded-2xl border border-white/10 bg-[#131316] p-5 space-y-3">
            <div className="flex items-center justify-between border-b border-white/10 pb-2">
              <h2 className="font-bold text-white text-sm">Table 4: Eligible Input Tax Credit (ITC) Reconciled from GSTR-2B</h2>
              <span className="text-[11px] font-mono text-emerald-400">Section 16 & 17 Compliant</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs font-mono">
              <div className="rounded-xl border border-white/5 bg-[#0e1015] p-3 space-y-1">
                <div className="text-[10px] font-sans text-slate-400 uppercase">4(A)(5) All Other ITC (Matched in 2B)</div>
                <div className="text-base font-bold text-emerald-400">₹{gstr3b.table4_eligible_itc.all_other_itc_2b.toFixed(2)}</div>
                <div className="text-[10px] font-sans text-slate-500">Auto-matched from purchase invoices</div>
              </div>

              <div className="rounded-xl border border-white/5 bg-[#0e1015] p-3 space-y-1">
                <div className="text-[10px] font-sans text-slate-400 uppercase">4(A)(3) Inward RCM Credit</div>
                <div className="text-base font-bold text-cyan-400">₹{gstr3b.table4_eligible_itc.inward_rcm.toFixed(2)}</div>
                <div className="text-[10px] font-sans text-slate-500">Self-assessed RCM credit</div>
              </div>

              <div className="rounded-xl border border-rose-500/20 bg-rose-950/10 p-3 space-y-1">
                <div className="text-[10px] font-sans text-rose-400 uppercase">4(B)(1) Ineligible under Sec 17(5)</div>
                <div className="text-base font-bold text-rose-400">₹{gstr3b.table4_ineligible_itc_17_5.toFixed(2)}</div>
                <div className="text-[10px] font-sans text-rose-500/80">Blocked credit automatically removed</div>
              </div>

              <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-3 space-y-1">
                <div className="text-[10px] font-sans text-emerald-300 uppercase">4(C) Net ITC Available</div>
                <div className="text-base font-bold text-emerald-300">₹{gstr3b.net_itc_available.toFixed(2)}</div>
                <div className="text-[10px] font-sans text-emerald-500/80">Available to offset output liability</div>
              </div>
            </div>
          </div>

          {/* Table 6.1 Payment of Tax (Cross-Utilization Offset) */}
          <div className="rounded-2xl border border-white/10 bg-[#131316] p-5 space-y-3">
            <div className="flex items-center justify-between border-b border-white/10 pb-2">
              <h2 className="font-bold text-white text-sm">Table 6.1: Payment of Tax & Statutory Cross-Utilization (Section 49(5))</h2>
              <span className="text-[11px] font-mono text-amber-400">Calculated Cash Liability</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-mono">
              <div className="rounded-xl border border-white/5 bg-[#0e1015] p-3 text-center">
                <div className="text-[10px] font-sans text-slate-400 uppercase">Net IGST Payable</div>
                <div className="text-lg font-bold text-white mt-1">₹{gstr3b.net_tax_payable.igst.toFixed(2)}</div>
              </div>
              <div className="rounded-xl border border-white/5 bg-[#0e1015] p-3 text-center">
                <div className="text-[10px] font-sans text-slate-400 uppercase">Net CGST Payable</div>
                <div className="text-lg font-bold text-white mt-1">₹{gstr3b.net_tax_payable.cgst.toFixed(2)}</div>
              </div>
              <div className="rounded-xl border border-white/5 bg-[#0e1015] p-3 text-center">
                <div className="text-[10px] font-sans text-slate-400 uppercase">Net SGST Payable</div>
                <div className="text-lg font-bold text-white mt-1">₹{gstr3b.net_tax_payable.sgst.toFixed(2)}</div>
              </div>
              <div className="rounded-xl border border-cyan-500/30 bg-cyan-950/20 p-3 text-center">
                <div className="text-[10px] font-sans text-cyan-300 uppercase">Total Cash Required</div>
                <div className="text-lg font-bold text-cyan-300 mt-1">
                  ₹{(gstr3b.net_tax_payable.igst + gstr3b.net_tax_payable.cgst + gstr3b.net_tax_payable.sgst).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: GSTR-1 Overview */}
      {activeTab === 'GSTR-1' && (
        <div className="rounded-2xl border border-white/10 bg-[#131316] p-5 space-y-4 text-xs font-sans">
          <div className="flex items-center justify-between border-b border-white/10 pb-2">
            <h2 className="font-bold text-white text-sm">GSTR-1 Outward Supplies Matrix</h2>
            <span className="font-mono text-cyan-400">Total Taxable: ₹{gstr1.totalTaxable.toLocaleString('en-IN')}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 font-mono">
            <div className="rounded-xl border border-white/5 bg-[#0e1015] p-3">
              <div className="text-[10px] font-sans text-slate-400 uppercase">B2B Supplies (Table 4)</div>
              <div className="text-lg font-bold text-white mt-1">{gstr1.b2bCount} Invoices</div>
              <div className="text-[11px] text-slate-400 mt-0.5">₹{gstr1.b2bTaxable.toFixed(2)} Taxable &bull; ₹{gstr1.b2bTax.toFixed(2)} Tax</div>
            </div>

            <div className="rounded-xl border border-white/5 bg-[#0e1015] p-3">
              <div className="text-[10px] font-sans text-slate-400 uppercase">B2C Supplies (Table 5 & 7)</div>
              <div className="text-lg font-bold text-white mt-1">{gstr1.b2cCount} Invoices</div>
              <div className="text-[11px] text-slate-400 mt-0.5">₹{gstr1.b2cTaxable.toFixed(2)} Taxable &bull; ₹{gstr1.b2cTax.toFixed(2)} Tax</div>
            </div>

            <div className="rounded-xl border border-white/5 bg-[#0e1015] p-3">
              <div className="text-[10px] font-sans text-slate-400 uppercase">HSN Code Summary (Table 12)</div>
              <div className="text-lg font-bold text-cyan-400 mt-1">{gstr1.hsnSummary.length} Unique HSNs</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Formatted with UQC and GST rates</div>
            </div>
          </div>
        </div>
      )}

      {/* Direct GSP Filing Modal */}
      {showGspModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-lg rounded-2xl border border-[rgba(247,247,248,0.14)] bg-[#131316] p-6 shadow-2xl space-y-4 text-xs font-sans">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-cyan-400" />
                <div>
                  <h3 className="font-bold text-white text-sm">Direct GSP Return Filing (GSTN API)</h3>
                  <p className="text-[11px] text-slate-400">GSTIN: {activeGstin} &bull; Return: {activeTab} ({fp})</p>
                </div>
              </div>
              <button
                onClick={() => setShowGspModal(false)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            {/* Step Indicators */}
            <div className="flex items-center justify-between px-2 text-[10px] font-bold text-slate-400">
              <span className={filingStep === 'AUTH' ? 'text-cyan-400' : 'text-slate-500'}>1. Portal Auth</span>
              <span>→</span>
              <span className={filingStep === 'VALIDATE' ? 'text-cyan-400' : 'text-slate-500'}>2. Save & Validate</span>
              <span>→</span>
              <span className={filingStep === 'EVC' ? 'text-cyan-400' : 'text-slate-500'}>3. Sign EVC</span>
              <span>→</span>
              <span className={filingStep === 'SUCCESS' ? 'text-emerald-400' : 'text-slate-500'}>4. ARN Receipt</span>
            </div>

            {/* STEP 1: AUTH */}
            {filingStep === 'AUTH' && (
              <div className="space-y-3">
                <div>
                  <label className="text-[11px] text-slate-300 font-semibold mb-1 block">GST Portal API Username</label>
                  <input
                    type="text"
                    value={gspUsername}
                    onChange={(e) => setGspUsername(e.target.value)}
                    placeholder="Enter GST Portal Username"
                    className="w-full rounded-xl border border-white/10 bg-[#0e1015] p-2.5 text-white font-mono focus:border-cyan-500 focus:outline-none"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-slate-400">Registered with GSTN: mobile ending in ***123</span>
                  <button
                    onClick={handleRequestOtp}
                    disabled={filingLoading}
                    className="rounded-lg bg-white/10 hover:bg-white/20 text-cyan-300 px-2.5 py-1 text-[11px] font-bold transition disabled:opacity-50"
                  >
                    Request OTP ⚡
                  </button>
                </div>

                <div>
                  <label className="text-[11px] text-slate-300 font-semibold mb-1 block">Enter 6-digit Portal OTP</label>
                  <input
                    type="text"
                    maxLength={6}
                    value={portalOtp}
                    onChange={(e) => setPortalOtp(e.target.value)}
                    placeholder="e.g. 123456"
                    className="w-full rounded-xl border border-white/10 bg-[#0e1015] p-2.5 text-center font-mono text-lg tracking-widest text-white focus:border-cyan-500 focus:outline-none"
                  />
                </div>

                <button
                  onClick={handleAuthenticateSession}
                  disabled={filingLoading || portalOtp.length < 4}
                  className="w-full rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 py-2.5 font-bold text-black transition hover:opacity-90 disabled:opacity-40"
                >
                  {filingLoading ? 'Authenticating with GSTN...' : 'Authenticate GSP Session →'}
                </button>
              </div>
            )}

            {/* STEP 2: VALIDATE */}
            {filingStep === 'VALIDATE' && (
              <div className="space-y-3">
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-950/10 p-3 space-y-1">
                  <div className="font-bold text-emerald-400 flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>GSP Session Active</span>
                  </div>
                  <div className="text-[11px] text-slate-300">
                    Target Return: <strong>{activeTab}</strong> for period <strong>{taxPeriod} {financialYear}</strong>.
                  </div>
                </div>

                <div className="text-[11px] text-slate-300 leading-relaxed">
                  Clicking below will upload the validated {activeTab} JSON payload to GSTN and execute the statutory <strong>Proceed-To-File (RETNEWPTF)</strong> liability check.
                </div>

                <button
                  onClick={handleUploadAndProceedToFile}
                  disabled={filingLoading}
                  className="w-full rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 py-2.5 font-bold text-black transition hover:opacity-90 disabled:opacity-40"
                >
                  {filingLoading ? 'Validating on GST Portal...' : `Upload & Validate ${activeTab} →`}
                </button>
              </div>
            )}

            {/* STEP 3: EVC OTP */}
            {filingStep === 'EVC' && (
              <div className="space-y-3">
                <p className="text-slate-300 leading-relaxed text-[11px]">
                  The GSTN portal has accepted the return payload. Please enter the 6-digit Aadhaar / Signatory EVC code received to digitally sign and complete filing:
                </p>
                <input
                  type="text"
                  maxLength={6}
                  value={evcOtp}
                  onChange={(e) => setEvcOtp(e.target.value)}
                  placeholder="e.g. 987654"
                  className="w-full rounded-xl border border-white/10 bg-[#0e1015] p-3 text-center font-mono text-lg tracking-widest text-white focus:border-cyan-500 focus:outline-none"
                />

                <button
                  onClick={handleFinalSignWithEvc}
                  disabled={filingLoading || evcOtp.length < 4}
                  className="w-full rounded-xl bg-gradient-to-r from-emerald-400 to-cyan-500 py-2.5 font-bold text-black transition hover:opacity-90 disabled:opacity-40"
                >
                  {filingLoading ? 'Filing Return with GSTN...' : 'Sign with EVC & File Return ✓'}
                </button>
              </div>
            )}

            {/* STEP 4: SUCCESS */}
            {filingStep === 'SUCCESS' && (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-5 text-center space-y-3">
                <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
                <div className="font-bold text-white text-base">Return Successfully Filed with GSTN!</div>
                <div className="text-slate-300 text-xs">
                  Government Acknowledgement Reference Number (ARN):
                </div>
                <div className="font-mono text-cyan-300 text-base font-bold bg-black/40 p-2.5 rounded-lg border border-cyan-500/30 select-all">
                  {generatedArn}
                </div>
                <div className="text-[11px] text-slate-400">
                  Filing Timestamp: {new Date(filingDate).toLocaleString('en-IN')} &bull; Status: FILED
                </div>
                <button
                  onClick={() => setShowGspModal(false)}
                  className="rounded-xl bg-white/10 hover:bg-white/20 text-white px-4 py-2 font-bold transition text-xs"
                >
                  Done & Close Window
                </button>
              </div>
            )}

            {filingStatusMsg && filingStep !== 'SUCCESS' && (
              <div className="text-center text-cyan-300 text-[11px] font-mono animate-pulse">
                {filingStatusMsg}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
